/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\DashboardPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RGL, { WidthProvider } from 'react-grid-layout';

// Cast to any to avoid type errors with newer react-grid-layout
const GridLayout = RGL as any;

// Define LayoutItem type for react-grid-layout compatibility
interface GridLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
}
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  LayoutGrid,
  Plus,
  Settings,
  LogOut,
  Monitor,
  Save,
  Pencil,
  User,
  Phone,
  Wifi,
  WifiOff,
  AlertTriangle
} from 'lucide-react';
import ErrorBoundary from '../../components/ErrorBoundary';

import { useAuthStore } from '../../store/auth';
import { useI18n } from '../../hooks/useI18n';
import { useThemeStore } from '../../store/theme';
import { useDashboardLayouts } from './hooks/useDashboardLayouts';
import { useSidebarContext } from '../../contexts/SidebarContext';

// Widgets
import { WidgetWrapper } from './components/WidgetWrapper';
import { AddWidgetModal } from './components/AddWidgetModal';
import { SystemStatsWidget } from './widgets/SystemStatsWidget';
import { ServicesStatusWidget } from './widgets/ServicesStatusWidget';
import { TelephonyStatsWidget } from './widgets/TelephonyStatsWidget';
import { QuickActionsWidget } from './widgets/QuickActionsWidget';
import { ServerInfoWidget } from './widgets/ServerInfoWidget';
import { HardwareInfoWidget } from './widgets/HardwareInfoWidget';
import { NetworkInfoWidget } from './widgets/NetworkInfoWidget';
import { AuditLogWidget } from './widgets/AuditLogWidget';
import { SofiaStatusWidget } from './widgets/SofiaStatusWidget';

// Create responsive grid layout with automatic width (same pattern as Switchboard)
// @ts-ignore - WidthProvider types don't properly include children prop
const ResponsiveGridLayout = WidthProvider(GridLayout) as any;

interface Widget {
  id: string;
  widgetType: string;
  title: string;
  config: Record<string, any>;
  gridItem: { x: number; y: number; w: number; h: number };
}

// -----------------------------------------------------------------------------
// ?? DashboardContent (Internal)
//    - Contains all the heavy logic: hooks, state, grid layout, widgets.
//    - ONLY rendered if the URL is /dashboard (safe from zombie navigation).
// -----------------------------------------------------------------------------
import { useDashboardEditStore } from '../../store/dashboardEdit';
import { dashboardEvents } from '../../components/header/DashboardControls';

// ... (keep existing imports)

// -----------------------------------------------------------------------------
// ?? DashboardWidgetItem
//    Wrapper to handle individual widget state (config handlers) vs Global Grid
// -----------------------------------------------------------------------------
const DashboardWidgetItem = ({
  widget,
  isEditMode,
  onDelete,
  onUpdate
}: {
  widget: Widget,
  isEditMode: boolean,
  onDelete: (id: string) => void,
  onUpdate: (id: string, updates: Partial<Widget>) => void
}) => {

  const [openConfigModal, setOpenConfigModal] = useState<(() => void) | undefined>(undefined);

  const handleConfigChange = (newConfig: Record<string, any>) => {
    onUpdate(widget.id, { config: newConfig });
  };

  const renderWidgetContent = () => {
    const props = {
      config: widget.config,
      isEditMode, // Pass edit mode to widgets
      onConfigChange: handleConfigChange,
      registerConfigHandler: useCallback((handler: () => void) => {
        setOpenConfigModal(() => handler);
      }, [])
    };

    switch (widget.widgetType) {
      case 'system_stats':
        return <SystemStatsWidget {...props} />;
      case 'services_status':
        return <ServicesStatusWidget {...props} />;
      case 'telephony_stats':
        return <TelephonyStatsWidget {...props} />;
      case 'quick_actions':
        return <QuickActionsWidget {...props} />;
      case 'server_info':
        return <ServerInfoWidget {...props} />;
      case 'network_info':
        return <NetworkInfoWidget {...props} />;
      case 'hardware_info':
        return <HardwareInfoWidget {...props} />;
      case 'audit_log':
        return <AuditLogWidget {...props} />;
      case 'sofia_status':
        return <SofiaStatusWidget {...props} />;
      default:
        return <div className="p-4 text-red-500">Unknown widget type: {widget.widgetType}</div>;
    }
  };

  return (
    <WidgetWrapper
      title={widget.title}
      widgetType={widget.widgetType}
      isEditMode={isEditMode}
      onDelete={() => onDelete(widget.id)}
      onConfigure={openConfigModal}
    >
      <ErrorBoundary
        fallback={
          <div className="flex flex-col items-center justify-center h-full text-red-500 p-4 text-center">
            <AlertTriangle className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-sm font-medium">Widget Error</p>
            <p className="text-xs opacity-70">Check console for details</p>
          </div>
        }
      >
        {renderWidgetContent()}
      </ErrorBoundary>
    </WidgetWrapper>
  );
};

// -----------------------------------------------------------------------------
// ?? DashboardContent (Internal)
// -----------------------------------------------------------------------------
function DashboardContent() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { themePrefs } = useThemeStore();

  // Global Store
  const { isEditMode, showAddModal, setShowAddModal } = useDashboardEditStore();
  const { isMobileView } = useSidebarContext();

  // Custom hook for layout management
  const {
    currentLayout,
    layouts,
    loading,
    saving,
    addWidget,
    deleteWidget,
    updateWidget,
    updateLayoutPositions,
    setActiveLayout,
    createLayout,
    deleteLayout,
    getOrCreateDefault,
    loadConfig, // Exposed for refreshing
    saveLayout,
    widgetTypes
  } = useDashboardLayouts();



  // Safety ref to prevent infinite loops if default creation fails
  const attemptDefaultCreationRef = useRef(false);

  // Initialize: Ensure we have a layout
  useEffect(() => {
    if (!loading && !currentLayout && layouts.length === 0 && !attemptDefaultCreationRef.current) {

      attemptDefaultCreationRef.current = true;
      getOrCreateDefault();
    }
  }, [loading, currentLayout, layouts.length, getOrCreateDefault]);

  // Listen for layout changes from Header
  useEffect(() => {
    const handleLayoutChanged = () => {

      loadConfig();
    };

    // Also listen for widget addition events if DashboardControls emits them directly without store?
    // DashboardControls emits 'openAddWidgetModal' but we use store for that now.
    // But we should listen to 'layoutChanged' which happens after create/delete/switch layout.

    const unsub = dashboardEvents.on('layoutChanged', handleLayoutChanged);
    return () => {
      unsub();
    };
  }, [loadConfig]);

  // Listen for Save Request
  useEffect(() => {
    const handleSaveLayout = () => {
      saveLayout();
    };

    const unsub = dashboardEvents.on('saveLayout', handleSaveLayout);
    return () => unsub();
  }, [saveLayout]);

  // Keep a ref to currentLayout to break the dependency cycle for handleLayoutChange
  const currentLayoutRef = useRef(currentLayout);
  useEffect(() => {
    currentLayoutRef.current = currentLayout;
  }, [currentLayout]);

  // Handle Grid Layout Change - Stable Callback
  const handleLayoutChange = useCallback((newLayout: GridLayoutItem[]) => {

    const layout = currentLayoutRef.current;
    if (!layout) {

      return;
    }

    // Extract changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updates = newLayout.map((item: any) => ({
      id: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h
    }));

    // Send to hook to update state and save
    updateLayoutPositions(updates);
  }, [updateLayoutPositions]);

  // Handle Widget Add
  const handleAddWidget = async (typeKey: string, name?: string) => {
    if (typeKey) {
      await addWidget(typeKey, name);
      setShowAddModal(false);
      // Notify Controls to refresh if they need to know about widgets (they technically don't, but good for consistency)
      dashboardEvents.emit('refreshControls');
    }
  };

  if (loading && !currentLayout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-background flex flex-col">
      {/* Header Removed - Controls are now in Main Header */}

      {/* Main Grid Content */}
      <main className="flex-1 p-2 overflow-y-auto">
        {currentLayout && currentLayout.widgets && currentLayout.widgets.length > 0 ? (() => {
          if (isMobileView) {
            // Sort widgets visually: first by y, then by x
            const sortedWidgets = [...currentLayout.widgets].sort((a, b) => {
              const yDiff = (a.gridItem?.y || 0) - (b.gridItem?.y || 0);
              if (yDiff !== 0) return yDiff;
              return (a.gridItem?.x || 0) - (b.gridItem?.x || 0);
            });

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 w-full">
                {sortedWidgets.map((widget) => (
                  <div
                    key={widget.id}
                    style={{ height: `${(widget.gridItem?.h || 20) * 10}px`, minHeight: '180px' }}
                    className="w-full p-[2px]"
                  >
                    <DashboardWidgetItem
                      widget={widget as unknown as Widget}
                      isEditMode={isEditMode}
                      onDelete={deleteWidget}
                      onUpdate={updateWidget}
                    />
                  </div>
                ))}
              </div>
            );
          }

          // Build layout array from widgets
          const layout = currentLayout.widgets.map((widget) => ({
            i: widget.id,
            x: widget.gridItem?.x || 0,
            y: widget.gridItem?.y || 0,
            w: widget.gridItem?.w || 4,
            h: widget.gridItem?.h || 3,
            minW: 1,
            minH: 1,
          }));


          return (
            <ResponsiveGridLayout
              className="layout"
              cols={48}
              rowHeight={10}
              layout={layout}
              draggableHandle=".widget-header"
              onLayoutChange={handleLayoutChange}
              measureBeforeMount={false}
              isDraggable={true}
              isResizable={true}
              margin={[0, 0]}
            >
              {currentLayout.widgets.map((widget) => (
                <div key={widget.id} className="p-[2px]">
                  <DashboardWidgetItem
                    widget={widget as unknown as Widget}
                    isEditMode={isEditMode}
                    onDelete={deleteWidget}
                    onUpdate={updateWidget}
                  />
                </div>
              ))}
            </ResponsiveGridLayout>
          );
        })() : (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <LayoutGrid className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">{t('dashboard.noWidgets', 'No widgets found')}</h2>
            {isEditMode && (
              <button
                onClick={() => setShowAddModal(true)}
                className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md flex items-center gap-1.5 transition-colors mt-4 text-sm"
              >
                <Plus className="w-5 h-5" />
                {t('dashboard.addWidget', 'Add Widget')}
              </button>
            )}
          </div>
        )}
      </main>

      {/* Add Widget Modal */}
      <AddWidgetModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddWidget={handleAddWidget}
        widgetTypes={widgetTypes}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// ??? DashboardPage (Wrapper)
//    - The Safe Navigation Guard.
//    - Returns NULL hooks/render if not on /dashboard path.
// -----------------------------------------------------------------------------
export default function DashboardPage() {
  const location = useLocation();

  // Strict Check: Only render content if we are explicitly on the dashboard
  // This prevents "zombie" instances from sticking around during navigation overrides
  if (location.pathname !== '/dashboard' && location.pathname !== '/') {
    return null;
  }

  return <DashboardContent />;
}
